import {
  ClassroomRoute,
  OrgsRoute,
  RootPath,
  SingleOrgDashboardRoute,
  UsersRoute,
} from "@lib/routes";
import {
  serializeOutcomesParams,
  serializeParams,
} from "./search-params-parsers";

/**
 * Creates a navigation link object with query parameters and optional state.
 * Simple parameters go in URL for shareability, complex objects go in state.
 *
 * @param {string} pathname - The destination pathname
 * @param {Object} search - Parameters to include in query string
 * @param {Object} state - Optional state for complex data that can't be serialized
 * @param {(values: Object) => string} serialize - Serializer for the search
 *   params; a view family injects its own schema-bound serializer so only that
 *   family's params reach the URL.
 * @returns {Object} Navigation link object with pathname, search, and optionally state
 */
export const createNavigationLink = (
  pathname,
  search = {},
  state = null,
  serialize = serializeParams
) => {
  const searchParams = serialize(search);

  const link = {
    pathname,
    search: searchParams,
  };

  // Only add state if provided and not empty
  if (state && Object.keys(state).length > 0) {
    link.state = state;
  }

  return link;
};

/**
 * Creates a user detail link with query parameters
 *
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createUserDetailLink = ({ userId, search, state }) => {
  return createNavigationLink(
    `${RootPath}${UsersRoute.path}/${userId}`,
    search,
    state
  );
};

/**
 * Creates a classroom detail link with query parameters
 *
 * @param {Object} params
 * @param {string} params.classroomId - Classroom ID
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createClassroomDetailLink = ({ classroomId, search, state }) => {
  return createNavigationLink(
    `${RootPath}${ClassroomRoute.path}/${classroomId}`,
    search,
    state
  );
};

/**
 * Creates a dashboard link with query parameters
 *
 * @param {Object} params
 * @param {string} [params.orgCode] - Organization code
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createDashboardLink = ({ orgCode, search, state }) => {
  const path = orgCode
    ? `${RootPath}${OrgsRoute.path}/${orgCode}`
    : `${RootPath}${SingleOrgDashboardRoute.path}`;

  return createNavigationLink(path, search, state);
};

/**
 * Creates an organizations link with query parameters
 *
 * @param {Object} [params]
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createOrganizationsLink = ({ search, state } = {}) => {
  return createNavigationLink(`${RootPath}${OrgsRoute.path}`, search, state);
};

/**
 * Creates a classrooms link with query parameters
 *
 * @param {Object} [params]
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createClassroomsLink = ({ search, state } = {}) => {
  return createNavigationLink(
    `${RootPath}${ClassroomRoute.path}`,
    search,
    state
  );
};

/**
 * Creates a users link with query parameters
 *
 * @param {Object} [params]
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createUsersLink = ({ search, state } = {}) => {
  return createNavigationLink(`${RootPath}${UsersRoute.path}`, search, state);
};

/**
 * Creates a my work link with query parameters
 *
 * @param {Object} [params]
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createMyWorkLink = ({ search, state } = {}) => {
  return createNavigationLink(`${RootPath}/my-work`, search, state);
};

/**
 * Creates an activity player link with query parameters
 *
 * @param {Object} [params]
 * @param {string} params.lessonId - Lesson ID
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data (e.g., courseData)
 * @returns {Object} Navigation link object
 */
export const createActivityPlayerLink = ({ lessonId, search, state } = {}) => {
  return createNavigationLink(
    `${RootPath}/activity-player/${lessonId}`,
    search,
    state
  );
};

/**
 * Creates a link to the outcome-reporting portfolio root (the multi-org
 * network landing above any single org).
 *
 * @param {Object} [params]
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createOutcomePortfolioLink = ({ search, state } = {}) => {
  return createNavigationLink(
    `${RootPath}/dashboard`,
    search,
    state,
    serializeOutcomesParams
  );
};

/**
 * Creates an outcome-reporting org detail link.
 *
 * @param {Object} [params]
 * @param {string} params.orgCode - Organization code
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createOutcomeOrgLink = ({ orgCode, search, state } = {}) => {
  return createNavigationLink(
    `${RootPath}/dashboard/org/${encodeURIComponent(orgCode)}`,
    search,
    state,
    serializeOutcomesParams
  );
};

/**
 * Creates an outcome-reporting classroom detail link.
 *
 * @param {Object} [params]
 * @param {string} params.orgCode - Organization code
 * @param {string} params.classroomId - Classroom ID
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createOutcomeClassroomLink = ({
  orgCode,
  classroomId,
  search,
  state,
} = {}) => {
  return createNavigationLink(
    `${RootPath}/dashboard/org/${encodeURIComponent(
      orgCode
    )}/classroom/${encodeURIComponent(classroomId)}`,
    search,
    state,
    serializeOutcomesParams
  );
};

/**
 * Creates an outcome-reporting learner detail link.
 *
 * @param {Object} [params]
 * @param {string} params.orgCode - Organization code
 * @param {string} params.classroomId - Classroom ID
 * @param {string} params.learnerId - Learner ID
 * @param {Object} [params.search] - Search parameters
 * @param {Object} [params.state] - Optional state for complex data
 * @returns {Object} Navigation link object
 */
export const createOutcomeLearnerLink = ({
  orgCode,
  classroomId,
  learnerId,
  search,
  state,
} = {}) => {
  return createNavigationLink(
    `${RootPath}/dashboard/org/${encodeURIComponent(
      orgCode
    )}/classroom/${encodeURIComponent(
      classroomId
    )}/learner/${encodeURIComponent(learnerId)}`,
    search,
    state,
    serializeOutcomesParams
  );
};
